﻿import { useState, useRef } from "react";
import { motion } from "motion/react";
import { useFormSubmit } from "../../hooks/useFormSubmit";
import Header from "../../components/feature/Header";
import Breadcrumbs from "../../components/feature/Breadcrumbs";
import Footer from "../../components/feature/Footer";
import WhatsAppFloating from "../../components/feature/WhatsAppFloating";
import CookieBanner from "../../components/feature/CookieBanner";
import CrossLinksFooter from "../../components/feature/CrossLinksFooter";
import SEO from "../../components/feature/SEO";

const asuntos = [
  "Presupuesto",
  "Reparación urgente",
  "Información",
  "Otro",
];

export default function Contacto() {
  const [agreed, setAgreed] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmit = useFormSubmit(agreed);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        ogImage="https://persianas-toldos-malaga.es/og/og-default.jpg"
        ogType="website"
        breadcrumbs={[
          { name: "Inicio", path: "/" },
          { name: "Contacto" },
        ]}
      />
      <Header />
      <Breadcrumbs />
      <main>
        {/* HERO */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-heading font-extrabold text-3xl md:text-5xl text-text-primary mb-5 leading-tight"
            >
              Contacta con nosotros
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="font-body text-lg text-text-secondary max-w-2xl mx-auto"
            >
              Estamos aquí para ayudarte. Llámanos, escríbenos o pásate por nuestro taller.
            </motion.p>
          </div>
        </section>

        {/* LAYOUT 2 COLUMNAS */}
        <section className="py-16 md:py-20">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              {/* IZQ - Info contacto */}
              <motion.div
                className="w-full lg:w-[45%]"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-heading font-extrabold text-2xl md:text-[28px] text-text-primary mb-8">
                  Información de contacto
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-5 bg-white border border-border rounded-2xl">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 text-xl shrink-0">
                      📞
                    </div>
                    <div>
                      <div className="font-body font-semibold text-base text-text-primary mb-0.5">Teléfono</div>
                      <a href="tel:+34951120770" className="font-body text-lg text-primary hover:text-primary-dark font-bold transition-colors">
                        951 12 07 70
                      </a>
                      <p className="font-body text-sm text-text-secondary mt-1">Lunes a Viernes: 9:00 - 19:00<br />Sábados: 9:00 - 14:00</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-5 bg-white border border-border rounded-2xl">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-whatsapp/10 text-xl shrink-0">
                      💬
                    </div>
                    <div>
                      <div className="font-body font-semibold text-base text-text-primary mb-0.5">WhatsApp</div>
                      <a
                        href="https://wa.me/34627213347?text=Hola%2C%20me%20interesa%20info"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-body text-lg text-whatsapp hover:text-[#128C7E] font-bold transition-colors"
                      >
                        627 21 33 47
                      </a>
                      <p className="font-body text-sm text-text-secondary mt-1">Respuesta en ~20 min, 24h</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-5 bg-white border border-border rounded-2xl">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary/10 text-xl shrink-0">
                      ✉️
                    </div>
                    <div>
                      <div className="font-body font-semibold text-base text-text-primary mb-0.5">Email</div>
                      <a
                        href="mailto:info@persianas-toldos-malaga.es"
                        className="font-body text-base text-secondary hover:text-secondary-dark font-semibold transition-colors"
                      >
                        info@persianas-toldos-malaga.es
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-5 bg-white border border-border rounded-2xl">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent/20 text-xl shrink-0">
                      📍
                    </div>
                    <div>
                      <div className="font-body font-semibold text-base text-text-primary mb-0.5">Dirección del taller</div>
                      <p className="font-body text-base text-text-secondary">
                        Calle Pacífico 54<br />
                        San Andrés, 29003 Málaga
                      </p>
                    </div>
                  </div>
                </div>

                {/* Horario taller */}
                <div className="mt-8 p-6 bg-surface-alt rounded-2xl border border-border">
                  <h3 className="font-heading font-bold text-base text-text-primary mb-4">
                    Horario del taller
                  </h3>
                  <div className="space-y-2 font-body text-sm text-text-secondary">
                    <div className="flex justify-between">
                      <span>Lunes - Viernes</span>
                      <span className="font-semibold text-text-primary">9:00 - 14:00 / 16:00 - 19:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sábados</span>
                      <span className="font-semibold text-text-primary">9:00 - 14:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Domingos</span>
                      <span className="font-semibold text-text-primary">Cerrado</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* DER - Formulario */}
              <motion.div
                className="w-full lg:w-[55%]"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                <div className="bg-white rounded-3xl p-6 md:p-10 shadow-sm border border-border">
                  <h2 className="font-heading font-extrabold text-2xl md:text-[28px] text-text-primary mb-2">
                    Escríbenos
                  </h2>
                  <p className="font-body text-sm text-text-secondary mb-8">
                    Te respondemos en menos de 24h.
                  </p>

                  <form
                      ref={formRef}
                      action="https://formspree.io/f/mdajpjzd"
                      method="POST"
                      encType="application/x-www-form-urlencoded"
                      data-readdy-form
                      id="contacto-form"
                      onSubmit={handleSubmit}
                      className="flex flex-col gap-5"
                    >
                      <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                      <div>
                        <label htmlFor="nombre" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                          Nombre <span className="text-primary">*</span>
                        </label>
                        <input
                          id="nombre"
                          name="nombre"
                          type="text"
                          required
                          className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                          placeholder="Tu nombre"
                        />
                      </div>

                      <div>
                        <label htmlFor="telefono" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                          Teléfono <span className="text-primary">*</span>
                        </label>
                        <div className="flex">
                          <span className="inline-flex items-center px-4 border border-r-0 border-border rounded-l-xl bg-slate-50 font-body text-sm text-text-secondary shrink-0">
                            +34
                          </span>
                          <input
                            id="telefono"
                            name="telefono"
                            type="tel"
                            required
                            className="w-full h-12 px-4 border border-border rounded-r-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                            placeholder="612 34 56 78"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="email" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                          Email
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                          placeholder="tu@email.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="asunto" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                          Asunto <span className="text-primary">*</span>
                        </label>
                        <select
                          id="asunto"
                          name="asunto"
                          required
                          className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all appearance-none"
                        >
                          <option value="">Selecciona...</option>
                          {asuntos.map((a) => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="mensaje" className="block font-body font-medium text-sm text-text-primary mb-1.5">
                          Mensaje <span className="text-primary">*</span>
                        </label>
                        <textarea
                          id="mensaje"
                          name="mensaje"
                          rows={5}
                          maxLength={500}
                          required
                          className="w-full px-4 py-3 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
                          placeholder="Cuéntanos qué necesitas..."
                        ></textarea>
                        <p className="mt-1 text-xs text-text-secondary font-body">Máximo 500 caracteres</p>
                      </div>

                      <div className="flex items-start gap-2.5">
                        <input
                          id="privacidad"
                          name="privacidad"
                          type="checkbox"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          className="mt-1 w-4 h-4 accent-primary shrink-0"
                        />
                        <div>
                          <label htmlFor="privacidad" className="font-body text-sm text-text-secondary leading-relaxed">
                            He leído y acepto la{" "}
                            <a href="/politica-privacidad" className="underline text-secondary hover:text-secondary-dark transition-colors">
                              Política de Privacidad
                            </a>{" "}
                            <span className="text-primary">*</span>
                          </label>
                          <p className="font-body text-xs text-text-secondary/70 mt-1 leading-relaxed">
                            Responsable: Persianas Toldos Málaga. Finalidad: responder a tu consulta y enviarte presupuesto. Legitimación: tu consentimiento. Destinatarios: no se cederán datos a terceros. Derechos: puedes ejercer tus derechos de acceso, rectificación y supresión escribiendo a info@persianas-toldos-malaga.es
                          </p>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="w-full h-14 bg-text-primary hover:bg-slate-800 text-white font-heading font-bold text-lg rounded-pill transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        Enviar <span>→</span>
                      </button>
                    </form>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* MAPA */}
        <section className="py-0">
          <div className="w-full h-[400px] border-t border-border">
            <iframe
              title="Ubicación Persianas Toldos Málaga"
              src="https://www.google.com/maps?q=Calle+Pacifico+54+San+Andres+Malaga&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
              allowFullScreen
            ></iframe>
          </div>
        </section>
      </main>
      <CrossLinksFooter />
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}